import { useEffect, useState } from "react";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar, User, Search, List, BookOpen, SlidersHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  published_at: string;
  featured_image: string | null;
  blog_categories: {
    name: string;
  } | null;
}

const Blog = () => {
  useSEO({
    title: 'Commerce Insights & Industry Trends | Ommni Blog',
    description: 'Stay updated with the latest trends in commerce operations, product information management, inventory optimization, and retail technology. Expert insights and best practices.',
    keywords: 'commerce blog, retail technology insights, PIM best practices, inventory management tips, ecommerce trends',
    canonical: 'https://ommni.io/blog',
  });

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    const { data, error } = await supabase
      .from("blog_posts")
      .select(`
        *,
        blog_categories (
          name
        )
      `)
      .eq("published", true)
      .order("published_at", { ascending: false });

    if (!error && data) {
      const sorted = [...data].sort((a, b) => {
        const at = a.published_at ? new Date(a.published_at).getTime() : 0;
        const bt = b.published_at ? new Date(b.published_at).getTime() : 0;
        return bt - at;
      });
      const uniqueByTitle = new Map<string, BlogPost>();
      for (const post of sorted) {
        if (!uniqueByTitle.has(post.title)) uniqueByTitle.set(post.title, post);
      }
      const uniquePosts = Array.from(uniqueByTitle.values());
      setPosts(uniquePosts);
      setFilteredPosts(uniquePosts);
      
      const uniqueCategories = Array.from(
        new Set(
          uniquePosts
            .map((post) => post.blog_categories?.name)
            .filter((name): name is string => !!name)
        )
      );
      setCategories(uniqueCategories);
      
      const counts: Record<string, number> = {};
      uniquePosts.forEach((post) => {
        if (post.blog_categories?.name) {
          counts[post.blog_categories.name] = (counts[post.blog_categories.name] || 0) + 1;
        }
      });
      setCategoryCounts(counts);
    } else {
      setPosts([]);
      setFilteredPosts([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    filterPosts();
  }, [searchQuery, selectedCategory, posts]);

  const filterPosts = () => {
    let filtered = [...posts];

    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (post) => post.blog_categories?.name === selectedCategory
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.excerpt.toLowerCase().includes(query)
      );
    }

    setFilteredPosts(filtered);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <main className="pt-32 px-4 pb-24">
          <div className="container mx-auto">
            <p className="text-center text-muted-foreground">Loading posts...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="pt-32 px-4 pb-24">
        <div className="container mx-auto">
          <div className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Explore What's Working in Commerce
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mb-8 mx-auto">
              Real-world insights, proven strategies, and actionable best practices from leading commerce operations teams
            </p>
            
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 bg-card border-border"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Desktop Sidebar */}
            <aside className="w-full lg:w-64 flex-shrink-0 hidden lg:block">
              <div className="sticky top-24 space-y-4">
                <Card className="p-4">
                  <h3 className="font-semibold text-lg mb-4">Categories</h3>
                  <nav className="space-y-1">
                    <button
                      onClick={() => setSelectedCategory("all")}
                      className={cn(
                        "w-full flex items-start gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left",
                        selectedCategory === "all"
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      )}
                    >
                      <List className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>All Articles</span>
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={cn(
                          "w-full flex items-start gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left",
                          selectedCategory === category
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-muted"
                        )}
                      >
                        <BookOpen className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{category}</span>
                      </button>
                    ))}
                  </nav>
                </Card>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              {/* Mobile Filter Button */}
              <div className="mb-6 lg:hidden">
                <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="w-full">
                      <SlidersHorizontal className="h-4 w-4 mr-2" />
                      Filter by Category
                      {selectedCategory !== "all" && (
                        <Badge variant="secondary" className="ml-2">1</Badge>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="h-[80vh]">
                    <SheetHeader>
                      <SheetTitle>Filter Articles</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <h3 className="font-semibold text-lg mb-4">Categories</h3>
                      <nav className="space-y-2">
                        <button
                          onClick={() => {
                            setSelectedCategory("all");
                            setMobileFilterOpen(false);
                          }}
                          className={cn(
                            "w-full flex items-start gap-2 px-4 py-3 rounded-lg text-sm transition-colors text-left",
                            selectedCategory === "all"
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted hover:bg-muted/80"
                          )}
                        >
                          <List className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>All Articles</span>
                        </button>
                        {categories.map((category) => (
                          <button
                            key={category}
                            onClick={() => {
                              setSelectedCategory(category);
                              setMobileFilterOpen(false);
                            }}
                            className={cn(
                              "w-full flex items-start gap-2 px-4 py-3 rounded-lg text-sm transition-colors text-left",
                              selectedCategory === category
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted hover:bg-muted/80"
                            )}
                          >
                            <BookOpen className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <span>{category}</span>
                          </button>
                        ))}
                      </nav>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>

              {filteredPosts.length > 0 && !searchQuery && selectedCategory === "all" && (
                <Link to={`/blog/${filteredPosts[0].slug}`} className="block mb-12">
                  <Card className="overflow-hidden gradient-card hover:shadow-2xl transition-all duration-500 group border-2 hover:border-primary/50">
                    <div className="grid lg:grid-cols-5 gap-0">
                      <div className="lg:col-span-2 h-64 lg:h-auto bg-gradient-hero relative overflow-hidden">
                        {filteredPosts[0].featured_image ? (
                          <img 
                            src={filteredPosts[0].featured_image} 
                            alt={filteredPosts[0].title}
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="absolute inset-0 bg-primary/10 group-hover:bg-primary/20 transition-colors duration-500" />
                        )}
                      </div>
                      <div className="lg:col-span-3 p-8 lg:p-12">
                        <span className="inline-block px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold mb-4">
                          Featured Article
                        </span>
                        <h2 className="text-2xl lg:text-4xl font-bold mb-4 group-hover:text-primary transition-colors leading-tight">
                          {filteredPosts[0].title}
                        </h2>
                        <p className="text-base text-muted-foreground mb-6 line-clamp-3">
                          {filteredPosts[0].excerpt}
                        </p>
                        <div className="flex items-center gap-6 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>{new Date(filteredPosts[0].published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span>Ommni Team</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              )}

              {filteredPosts.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm text-muted-foreground">
                    {searchQuery || selectedCategory !== "all" 
                      ? `Found ${filteredPosts.length} ${filteredPosts.length === 1 ? 'article' : 'articles'}`
                      : `Showing ${filteredPosts.length - 1} recent ${filteredPosts.length - 1 === 1 ? 'article' : 'articles'}`}
                  </p>
                </div>
              )}

              {filteredPosts.length === 0 && (
                <Card className="p-16 text-center bg-card/50">
                  <div className="max-w-md mx-auto">
                    <Search className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No articles found</h3>
                    <p className="text-sm text-muted-foreground">
                      {searchQuery || selectedCategory !== "all"
                        ? "Try adjusting your search or filters to find what you're looking for."
                        : "Check back soon for new content!"}
                    </p>
                  </div>
                </Card>
              )}

              {filteredPosts.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {(searchQuery || selectedCategory !== "all" ? filteredPosts : filteredPosts.slice(1)).map((post) => (
                    <Link key={post.id} to={`/blog/${post.slug}`}>
                      <Card className="overflow-hidden gradient-card hover:shadow-xl transition-all duration-300 group cursor-pointer h-full flex flex-col border-2 hover:border-primary/50">
                        <div className="h-48 bg-gradient-hero relative overflow-hidden">
                          {post.featured_image ? (
                            <img 
                              src={post.featured_image} 
                              alt={post.title}
                              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-primary/5 group-hover:bg-primary/10 transition-colors duration-300" />
                          )}
                        </div>
                        <div className="p-6 flex-1 flex flex-col">
                          {post.blog_categories && (
                            <Badge variant="outline" className="w-fit mb-3 text-xs">
                              {post.blog_categories.name}
                            </Badge>
                          )}
                          <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-4 border-t">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5" />
                              <span>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default Blog;
